import { ChevronLeft, Save, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Button as CatalystButton } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import {
  Field,
  Label as CatalystLabel,
} from '@/components/ui/catalyst/fieldset';
import { Textarea } from '@/components/ui/catalyst/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import useMatterSelection from '@/hooks/useMatterSelection';
import { usePatchMatterMutation } from '@/services/api';
import type { MatterInvestigationStatus } from '@/services/api/generated/policeReportsService';
import {
  useCreateIncidentMutation,
  useGetIncidentsQuery,
  useUpdateIncidentMutation,
} from '@/services/api/incidentService';
import { usePatchMatterClientMutation } from '@/services/api/mattersService';
import {
  usePoliceInvestigationListQuery,
  usePoliceInvestigationPartialUpdateMutation,
} from '@/services/api/policeInvestigationService';

interface DumbDataCollectionProps {
  onClose: () => void;
}

export const DumbDataCollection: React.FC<DumbDataCollectionProps> = ({
  onClose,
}) => {
  const { selectedMatterData, selectedMatterLoading } = useMatterSelection();
  const [searchParams, setSearchParams] = useSearchParams();

  // API hooks
  const [patchMatter] = usePatchMatterMutation();
  const [patchMatterClient] = usePatchMatterClientMutation();
  const [createIncident] = useCreateIncidentMutation();
  const [updateIncident] = useUpdateIncidentMutation();
  const [updateInvestigation] = usePoliceInvestigationPartialUpdateMutation();

  // Get incident data
  const { data: incidents } = useGetIncidentsQuery(
    { matterId: selectedMatterData?.id || '' },
    { skip: !selectedMatterData?.id },
  );

  const existingIncident = incidents?.[0];
  const primaryClient = selectedMatterData?.clients?.[0];

  // Derive initial values from data - this will update when data changes
  const initialDateOfLoss = selectedMatterData?.date_of_loss || '';
  const actualClientName = primaryClient?.name || '';
  const initialLocation = existingIncident?.location || '';

  // If no client name exists, prefill with matter name
  const shouldPrefillClientName = !actualClientName && selectedMatterData?.name;
  const prefilledClientName = shouldPrefillClientName
    ? selectedMatterData.name
    : actualClientName;
  const initialClientName = prefilledClientName;

  // Form state with proper initial values
  const [dateOfLoss, setDateOfLoss] = useState(() => initialDateOfLoss);
  const [clientName, setClientName] = useState(() => initialClientName);
  const [incidentLocation, setIncidentLocation] = useState(
    () => initialLocation,
  );
  const [isSaving, setIsSaving] = useState(false);

  // Reset form values when data changes (but only if form hasn't been modified)
  const [hasUserChanges, setHasUserChanges] = useState<boolean>(
    !!shouldPrefillClientName,
  );

  // Dialog state for "no report required"
  const [isNoReportDialogOpen, setIsNoReportDialogOpen] = useState(false);
  const [noReportDescription, setNoReportDescription] = useState('');

  // Get investigation data
  const { data: investigationData } = usePoliceInvestigationListQuery(
    { matterId: selectedMatterData?.id || '' },
    { skip: !selectedMatterData?.id },
  );

  // Backend returns single object (1-to-1 with matter), but codegen thinks it's array
  // Cast to handle both cases safely
  const investigation = Array.isArray(investigationData)
    ? investigationData[0]
    : investigationData;

  useEffect(() => {
    if (!hasUserChanges) {
      setDateOfLoss(initialDateOfLoss);
      setClientName(initialClientName);
      setIncidentLocation(initialLocation);
    }
  }, [initialDateOfLoss, initialClientName, initialLocation, hasUserChanges]);

  // Track changes from original values
  const hasChanges =
    dateOfLoss !== initialDateOfLoss ||
    clientName !== actualClientName || // Compare against actual client name, not prefilled
    incidentLocation !== initialLocation;

  // Handle field changes and mark as user-modified
  const handleDateOfLossChange = (value: string) => {
    setDateOfLoss(value);
    setHasUserChanges(true);
  };

  const handleClientNameChange = (value: string) => {
    setClientName(value);
    setHasUserChanges(true);
  };

  const handleIncidentLocationChange = (value: string) => {
    setIncidentLocation(value);
    setHasUserChanges(true);
  };

  const handleClose = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('showDDC');
    setSearchParams(newSearchParams);
    onClose();
  };

  const handleSave = async () => {
    if (!selectedMatterData || !hasChanges) return;

    setIsSaving(true);
    const promises: Promise<unknown>[] = [];
    let successCount = 0;

    try {
      // Update matter date_of_loss if changed
      if (dateOfLoss !== initialDateOfLoss) {
        promises.push(
          patchMatter({
            matter_id: selectedMatterData.id,
            patch: { date_of_loss: dateOfLoss || undefined },
          }).unwrap(),
        );
      }

      // Update client name if changed
      if (clientName !== actualClientName && primaryClient) {
        promises.push(
          patchMatterClient({
            matter_id: selectedMatterData.id,
            matter_client_id: primaryClient.id,
            patch: { name: clientName },
          }).unwrap(),
        );
      }

      // Update incident location if changed
      if (incidentLocation !== initialLocation) {
        if (existingIncident) {
          // Update existing incident
          promises.push(
            updateIncident({
              matterId: selectedMatterData.id,
              incidentId: existingIncident.id,
              incident: { location: incidentLocation || null },
            }).unwrap(),
          );
        } else if (incidentLocation.trim()) {
          // Create new incident with location
          promises.push(
            createIncident({
              matterId: selectedMatterData.id,
              incident: {
                matter_id: selectedMatterData.id,
                location: incidentLocation,
              },
            }).unwrap(),
          );
        }
      }

      // Execute all updates
      const results = await Promise.allSettled(promises);

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          successCount += 1;
        } else {
          // eslint-disable-next-line no-console
          console.error('Update failed:', result.reason);
        }
      });

      if (successCount > 0) {
        toast.success(
          `Successfully updated ${successCount} field${successCount > 1 ? 's' : ''}`,
        );
        // Reset user changes flag after successful save
        setHasUserChanges(false);
      }

      if (successCount < promises.length) {
        toast.error('Some updates failed. Please try again.');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Save failed:', error);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkNoReportRequired = async () => {
    if (!selectedMatterData?.id || !investigation?.id) return;

    try {
      await updateInvestigation({
        matterId: selectedMatterData.id,
        id: investigation.id,
        patchedUpdateMatterInvestigation: {
          id: investigation.id,
          status: 'not_applicable' as MatterInvestigationStatus,
          description: noReportDescription?.trim() || investigation.description,
        },
      }).unwrap();

      toast.success('Matter marked as no police report required');
      setIsNoReportDialogOpen(false);
      setNoReportDescription('');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to mark as no report required:', error);
      toast.error('Failed to update investigation status');
    }
  };

  if (selectedMatterLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-10 bg-gray-200 rounded" />
          <div className="h-10 bg-gray-200 rounded" />
          <div className="h-10 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50/30 border-l border-gray-200">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-auto text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft className="h-3 w-3" />
              <span>Close</span>
            </Button>
          </div>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            size="sm"
            className="flex items-center gap-1 bg-gray-900 hover:bg-gray-800 text-white text-xs transition-colors disabled:opacity-50"
          >
            <Save className="h-3 w-3" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full mb-3">
            <span className="text-sm">⚠️</span>
          </div>
          <h2 className="text-base font-medium text-gray-800 mb-2">
            Missing Info
          </h2>
          <p className="text-xs text-gray-500 leading-relaxed max-w-xs mx-auto">
            Can&apos;t proceed with investigating police reports without the
            below information
          </p>
        </div>

        <div className="space-y-3">
          {/* Date of Loss */}
          <div className="bg-white rounded-md p-3 border border-gray-100">
            <Label
              htmlFor="date-of-loss"
              className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1.5"
            >
              <span className="text-xs opacity-60">📅</span>
              Date of Loss
            </Label>
            <Input
              id="date-of-loss"
              type="date"
              value={dateOfLoss}
              onChange={(e) => handleDateOfLossChange(e.target.value)}
              className="text-sm border-gray-200 focus:border-gray-400 focus:ring-gray-400 focus:ring-1 transition-colors"
              placeholder="Select date of loss"
            />
          </div>

          {/* Client Name */}
          <div className="bg-white rounded-md p-3 border border-gray-100">
            <Label
              htmlFor="client-name"
              className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1.5"
            >
              <span className="text-xs opacity-60">👤</span>
              Client Name
              {shouldPrefillClientName && (
                <span className="text-xs font-normal text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200">
                  from matter name
                </span>
              )}
            </Label>
            <Input
              id="client-name"
              type="text"
              value={clientName}
              onChange={(e) => handleClientNameChange(e.target.value)}
              className={`text-sm transition-colors ${
                shouldPrefillClientName
                  ? 'border-gray-300 bg-gray-50/50 focus:border-gray-400 focus:ring-gray-400 focus:ring-1'
                  : 'border-gray-200 focus:border-gray-400 focus:ring-gray-400 focus:ring-1'
              }`}
              placeholder="Enter client name"
            />
          </div>

          {/* Incident Location */}
          <div className="bg-white rounded-md p-3 border border-gray-100">
            <Label
              htmlFor="incident-location"
              className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1.5"
            >
              <span className="text-xs opacity-60">📍</span>
              Incident Location
            </Label>
            <textarea
              id="incident-location"
              value={incidentLocation}
              onChange={(e) => handleIncidentLocationChange(e.target.value)}
              className="w-full min-h-[70px] px-3 py-2 text-sm border border-gray-200 rounded focus:border-gray-400 focus:ring-1 focus:ring-gray-400 resize-none transition-colors placeholder:text-gray-400"
              placeholder="Enter detailed incident location (address, intersection, landmarks, etc.)"
              rows={3}
            />
          </div>
        </div>

        {hasChanges && (
          <div className="mt-4 p-2.5 bg-gray-50 border border-gray-200 rounded">
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-xs">💾</span>
              <p className="text-xs text-gray-600">
                You have unsaved changes. Click &quot;Save Changes&quot; to
                update the matter.
              </p>
            </div>
          </div>
        )}

        {/* OR separator and no report button */}
        <div className="mt-5 pt-4 border-t border-gray-100">
          <div className="text-center mb-3">
            <span className="text-xs font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded">
              OR
            </span>
          </div>
          <CatalystButton
            color="red"
            onClick={() => setIsNoReportDialogOpen(true)}
            className="w-full text-xs py-2 transition-colors"
            disabled={!investigation?.id}
          >
            <XCircle data-slot="icon" />
            Mark this matter as no report required
          </CatalystButton>
        </div>
      </div>

      {/* No Report Required Dialog */}
      <Dialog
        open={isNoReportDialogOpen}
        onClose={() => setIsNoReportDialogOpen(false)}
      >
        <DialogTitle>Mark as No Police Report Required</DialogTitle>
        <DialogDescription>
          Mark this matter as not requiring a police report investigation.
        </DialogDescription>
        <DialogBody>
          <Field>
            <CatalystLabel>Reason (optional)</CatalystLabel>
            <Textarea
              value={noReportDescription}
              onChange={(e) => setNoReportDescription(e.target.value)}
              placeholder="Explain why no police report is needed for this matter..."
              rows={3}
            />
          </Field>
        </DialogBody>
        <DialogActions>
          <CatalystButton
            outline
            onClick={() => setIsNoReportDialogOpen(false)}
          >
            Cancel
          </CatalystButton>
          <CatalystButton color="red" onClick={handleMarkNoReportRequired}>
            <XCircle data-slot="icon" />
            Mark as No Report Required
          </CatalystButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};
